import auth0 from 'auth0-js'

const KEY_ACCESS_TOKEN = 'access_token'
const KEY_ID_TOKEN = 'id_token'
const KEY_EXPIRES_AT = 'expires_at'

const auth = {
  domain: process.env.AUTH_DOMAIN,
  clientID: process.env.AUTH_CLIENT_ID,
  redirectUri: process.env.AUTH_REDIRECT_URI,
  audience: process.env.AUTH_AUDIENCE
}

const lockOption = {
  language: process.env.APP_LANGUAGE,
  logo: process.env.APP_PUBLIC_ICON,
  color: process.env.APP_COLOR,
  title: process.env.APP_NAME,
  placeholder: process.env.AUTH_EMAIL_PLACEHOLDER
}

export default {
  namespaced: true,
  state () {
    return {
      authenticated:
        !!localStorage.getItem(KEY_ACCESS_TOKEN) &&
        !!(new Date().getTime() < localStorage.getItem(KEY_EXPIRES_AT)),
      me: null,
      authorization: null,
      authorizer: new auth0.WebAuth({
        domain: auth.domain,
        clientID: auth.clientID,
        redirectUri: auth.redirectUri,
        audience: auth.audience,
        responseType: 'id_token token',
        scope: 'openid',
        leeway: 60
      })
    }
  },
  getters: {
    isAuthenticated (state) {
      return state.authenticated
    },
    authorization (state) {
      return state.authorization
    },
    isMeLoaded (state) {
      return state.authenticated && state.me && !!state.me.uuid
    },
    me (state) {
      return state.me
    },
    hasRole: (state, getters) => role => {
      if (!role) {
        return true
      }
      if (!getters.isMeLoaded) {
        return false
      }
      if (state.me.roles === undefined) {
        return false
      }
      return state.me.roles.includes(role)
    },
    hasRoles: (state, getters) => roles => {
      if (!roles) {
        return true
      }
      if (!getters.isMeLoaded) {
        return false
      }
      if (state.me.roles === undefined) {
        return false
      }
      return roles.map(role => state.me.roles.includes(role)).every(v => v)
    },
    hasAnyRoles: (state, getters) => roles => {
      if (!roles) {
        return true
      }
      if (!getters.isMeLoaded) {
        return false
      }
      if (state.me.roles === undefined) {
        return false
      }
      return roles.map(role => state.me.roles.includes(role)).some(v => v)
    },
    hasPermission: (state, getters) => permission => {
      if (!permission) {
        return true
      }
      if (!getters.isMeLoaded) {
        return false
      }
      if (state.me.permissions === undefined) {
        return false
      }
      return state.me.permissions.includes(permission)
    },
    hasPermissions: (state, getters) => permissions => {
      if (!permissions) {
        return true
      }
      if (!getters.isMeLoaded) {
        return false
      }
      if (state.me.permissions === undefined) {
        return false
      }
      return permissions
        .map(permission => state.me.permissions.includes(permission))
        .every(v => v)
    },
    hasAnyPermissions: (state, getters) => permissions => {
      if (!permissions) {
        return true
      }
      if (!getters.isMeLoaded) {
        return false
      }
      if (state.me.permissions === undefined) {
        return false
      }
      return permissions
        .map(permission => state.me.permissions.includes(permission))
        .some(v => v)
    }
  },
  mutations: {
    setMe (state, me) {
      state.me = me
    },
    shake (state) {
      state.authorization = `Bearer ${localStorage.getItem(KEY_ACCESS_TOKEN)}`
      state.authenticated =
        !!localStorage.getItem(KEY_ACCESS_TOKEN) &&
        !!(new Date().getTime() < localStorage.getItem(KEY_EXPIRES_AT))
      if (!state.authenticated) {
        state.me = null
        state.authorization = null
      }
    },
    saveAuth (state, authResult) {
      localStorage.setItem(KEY_ACCESS_TOKEN, authResult.accessToken)
      localStorage.setItem(KEY_ID_TOKEN, authResult.idToken)
      localStorage.setItem(
        KEY_EXPIRES_AT,
        authResult.expiresIn * 1000 + new Date().getTime()
      )
      state.authorization = `Bearer ${authResult.accessToken}`
    },
    clearAuth (state) {
      state.authenticated = false
      state.me = null
      state.authorization = null
      localStorage.removeItem(KEY_ACCESS_TOKEN)
      localStorage.removeItem(KEY_ID_TOKEN)
      localStorage.removeItem(KEY_EXPIRES_AT)
    }
  },
  actions: {
    authorize ({ commit, state }) {
      state.authorizer.authorize(lockOption)
    },
    signIn ({ commit, state }) {
      return new Promise((resolve, reject) => {
        state.authorizer.parseHash((error, authResult) => {
          if (error !== null) {
            return reject(error)
          }
          if (authResult === null) {
            return reject(new Error('Unexpected state.'))
          }
          commit('saveAuth', authResult)
          return resolve()
        })
      })
    },
    signOut ({ commit, state }) {
      commit('clearAuth')
      state.authorizer.logout({
        client_id: auth.clientID,
        returnTo: window.location.origin
      })
    },
    forceSignOut ({ commit, state }) {
      commit('clearAuth')
      state.authorizer.logout({
        client_id: auth.clientID,
        returnTo: `${window.location.origin}?_n=${new Date().getTime()}`
      })
    },
    refresh ({ commit, state }) {
      commit('shake')
      const renewToken = () => {
        return new Promise((resolve, reject) => {
          if (localStorage.getItem(KEY_ACCESS_TOKEN) === null) {
            return resolve()
          }
          if (new Date().getTime() <= localStorage.getItem(KEY_EXPIRES_AT)) {
            return resolve()
          }
          state.authorizer.checkSession({}, (error, authResult) => {
            if (error) {
              console.dir(error)
              commit('clearAuth')
              return reject(error)
            }
            if (authResult === null) {
              return reject(new Error('Unexpected state.'))
            }
            commit('saveAuth', authResult)
            commit('shake')
            return resolve()
          })
        })
      }
      const reload = () => {
        return new Promise((resolve, reject) => {
          const metadataUri = `https://${auth.domain.replace(
            /\./g,
            ':'
          )}/metadata`
          if (state.authenticated && state.me === null) {
            return state.authorizer.client.userInfo(
              localStorage.getItem(KEY_ACCESS_TOKEN),
              (error, user) => {
                if (error) {
                  console.dir(error)
                  commit('clearAuth')
                  return reject(error)
                }
                commit('setMe', user[metadataUri])
                return resolve()
              }
            )
          }
          return resolve()
        })
      }
      return renewToken().then(() => {
        return reload()
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/common/store/account.js